import { ProposalSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { OpenOrdersProducts } from '@components/BookingFlow/Stationary/OpenOrdersProducts';
import { ProposalContactPhoneNotice } from '@components/BookingProposal/ProposalContactPhoneNotice';
import { ProposalCustomerAndLocation } from '@components/BookingProposal/ProposalCustomerAndLocation';
import { ProposalShoppingCart } from '@components/BookingProposal/ProposalShoppingCart';
import { RejectButtonAndModal } from '@components/BookingProposal/RejectButtonAndModal';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { Trans } from '@lingui/react/macro';
import type { BookingProposalRecord } from '@navio/nsdk';
import { NoddiBadge, NoddiBasicCard, NoddiLink, NoddiSkeleton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { commonTrackingEvents, tracking, useCancelHoldOnExpiry } from 'noddi-web';
import { useState } from 'react';
import { useStationaryProposalCheckout } from '../../hooks/useStationaryProposalCheckout';
import { useBookingProposalActions, useBookingProposalAnalytics } from '../../stores/bookingProposalStore';
import { showsSuggestedProposalTimes } from '../../utils/stationaryProposalPolicy';
import { ConfirmStationaryProposalButton } from './ConfirmStationaryProposalButton';
import { ProposalSuggestedTimes } from './ProposalSuggestedTimes';
import { StationaryProposalCheckoutBody } from './StationaryProposalCheckoutBody';

type Props = {
  proposal: BookingProposalRecord;
};

export const StationaryProposalCard = ({ proposal }: Props) => {
  const { id, slug } = proposal;
  const checkout = useStationaryProposalCheckout(proposal);
  const { clearStore } = useBookingProposalActions();
  const analytics = useBookingProposalAnalytics();
  const [savedOrganizationNumber, setSavedOrganizationNumber] = useState(false);
  const isReservationExpired = useCancelHoldOnExpiry(checkout.stationaryReservation);
  const showsSuggestedTimes = showsSuggestedProposalTimes(proposal.serviceDepartment.brand.domain);
  const handleRejected = () => {
    tracking.track(commonTrackingEvents.proposalRejected, analytics);
    clearStore();
  };

  if (checkout.isPaymentOptionsLoading) {
    return (
      <NoddiBasicCard className='flex flex-col overflow-hidden p-0'>
        <NoddiSkeleton className='h-48 w-full' />
        <div className='flex flex-col gap-4 p-4 md:p-5'>
          <NoddiSkeleton className='h-5 w-1/2' />
          <NoddiSkeleton className='h-5 w-2/3' />
          <NoddiSkeleton className='h-40 w-full' />
          <NoddiSkeleton className='h-10 w-full' />
        </div>
      </NoddiBasicCard>
    );
  }

  // Without payment options checkout silently defaults to pay-before, even for invoice customers.
  if (checkout.paymentOptionsError) {
    return (
      <NoddiBasicCard className='p-4 md:p-5'>
        <ApiErrorMessageWithTrans
          error={checkout.paymentOptionsError}
          isRetrying={checkout.isPaymentOptionsFetching}
          onRetry={() => void checkout.refetchPaymentOptions()}
        />
      </NoddiBasicCard>
    );
  }

  // Suggested-times brands stay on this step even while a time is held, so back
  // navigation has a real page to land on instead of a redirect to the summary.
  if (showsSuggestedTimes || !checkout.stationaryReservation) {
    return (
      <NoddiBasicCard className='flex flex-col overflow-hidden p-0'>
        <div className='flex flex-col gap-4 p-4 md:p-5'>
          {showsSuggestedTimes && (
            <NoddiBadge variant='destructive' withCircle className='self-start'>
              <Trans>Needs confirmation</Trans>
            </NoddiBadge>
          )}
          <ProposalCustomerAndLocation
            customerName={checkout.customerName}
            customerPhoneNumber={checkout.customerPhoneNumber}
            locationName={checkout.locationName}
            locationAddress={checkout.locationAddress}
            sideBySide={showsSuggestedTimes}
          />

          {checkout.showsLoggedOutContactPhoneNotice && (
            <ProposalContactPhoneNotice phoneDisplay={checkout.displayedInvalidUserPhone} />
          )}

          <div className='flex flex-col gap-4 border-t border-border pt-4'>
            <ProposalShoppingCart
              userGroupId={proposal.userGroupMember.userGroup.id}
              serviceDepartmentId={proposal.serviceDepartment.id}
              bookingProposalItems={checkout.bookingProposalItems}
              catalogPrices={checkout.catalogPrices}
            />
            {/* Outstanding items on the car, priced into the checkout total rather than this one.
                The order summary above already lists them, so a zero total is pure noise here. */}
            <OpenOrdersProducts lines={checkout.includedOrderLines} hideWhenFree />
          </div>

          <ProposalSpikedTireSeasonBanner />

          {showsSuggestedTimes ? (
            // Reject sits in the suggestions' own action row rather than as a third
            // full-width block under the button.
            <ProposalSuggestedTimes
              proposal={proposal}
              secondaryAction={
                checkout.isRecallProposal ? (
                  <RejectButtonAndModal id={id} onRejected={handleRejected} className='w-auto' />
                ) : undefined
              }
            />
          ) : (
            <>
              <NoddiLink
                to={customerRoutes.stationaryProposalTimeWindow.getPath({ slug })}
                variant='primary'
                className='w-full'
                startIcon='Clock'
                onClick={() =>
                  tracking.track(commonTrackingEvents.proposalTimePickerOpened, { ...analytics, from: 'landing' })
                }
              >
                <Trans>Select time</Trans>
              </NoddiLink>
              {checkout.isRecallProposal && <RejectButtonAndModal id={id} onRejected={handleRejected} />}
            </>
          )}
        </div>
      </NoddiBasicCard>
    );
  }

  return (
    // Confirming from the landing card is a checkout: it needs the same fix sheet
    // as the summary page. No banner and no auto-open here — this card is a
    // landing surface, so the report only interrupts an actual confirm click.
    <CustomerDataQualityProvider
      userGroupId={checkout.bookingUserGroupId}
      bookingLocationType={proposal.bookingLocationType ?? undefined}
      countryCode={checkout.addressCountryCode}
    >
      <StationaryProposalCheckoutBody
        slug={slug}
        checkout={checkout}
        onOrganizationNumberSaved={() => setSavedOrganizationNumber(true)}
        afterSummary={
          <div className='flex flex-col'>
            <ConfirmStationaryProposalButton
              proposal={proposal}
              paymentOption={checkout.paymentOption}
              userGroupPayerId={checkout.invoiceToUserGroupId}
              totalAmount={checkout.totalAmount}
              hasValidPhone={!checkout.isLoggedIn || checkout.hasValidContactPhone}
              hasOrganizationNumber={
                !checkout.isInvoice || !checkout.requiresOrganizationNumber || savedOrganizationNumber
              }
              bookingUserGroupId={checkout.bookingUserGroupId}
              isReservationExpired={isReservationExpired}
            />
            {checkout.isRecallProposal && <RejectButtonAndModal id={id} onRejected={handleRejected} />}
          </div>
        }
      />
    </CustomerDataQualityProvider>
  );
};
